import { ThemeEvents, VariantUpdateEvent } from '@theme/events';

/**
 * A custom element that displays a product price.
 * This component listens for variant update events and updates the price display accordingly.
 * It handles price updates from two different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 */
class ProductPrice extends HTMLElement {
  connectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    console.log('[ProductPrice] connectedCallback', {
      element: this,
      productId: this.dataset.productId,
      blockId: this.dataset.blockId,
      closestSection: closestSection,
      hasSection: !!closestSection
    });
    
    if (!closestSection) {
      console.warn('[ProductPrice] No section found, cannot listen for variant updates');
      return;
    }
    
    console.log('[ProductPrice] Adding event listener for', ThemeEvents.variantUpdate);
    closestSection.addEventListener(ThemeEvents.variantUpdate, this.updatePrice);
  }

  disconnectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.removeEventListener(ThemeEvents.variantUpdate, this.updatePrice);
  }

  /**
   * Updates the price.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice = (event) => {
    console.log('[ProductPrice] updatePrice called', {
      event,
      currentElement: this,
      htmlInEvent: event.detail?.data?.html,
      productId: event.detail?.data?.productId,
      variantId: event.detail?.resource?.id
    });
    
    // Handle new product updates (for combined listings)
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    }
    
    // Verify this price element should update based on product ID
    // The event.detail.data.productId comes from the variant picker that triggered the update
    const eventProductId = event.detail.data.productId;
    const currentProductId = this.dataset.productId;
    const currentBlockId = this.dataset.blockId;
    
    console.log('[ProductPrice] Product ID check', {
      eventProductId,
      currentProductId,
      currentBlockId,
      willSkip: eventProductId && currentProductId && eventProductId !== currentProductId
    });
    
    // If product IDs don't match and we have both IDs, skip this price element
    if (eventProductId && currentProductId && eventProductId !== currentProductId) {
      console.log('[ProductPrice] Skipping - product IDs don\'t match');
      return;
    }

    // Find the matching product-price element in the fetched HTML by data-product-id and data-block-id
    // This ensures we update the correct price element when multiple price blocks exist
    // Use the event's product ID if available, otherwise fall back to current element's product ID
    const searchProductId = eventProductId || currentProductId;
    
    // Helper function to search for price element in a given container
    const findPriceElement = (container) => {
      if (currentBlockId && searchProductId) {
        // First try to match by both product-id and block-id for precise matching
        const match = container.querySelector(
          `product-price[data-product-id="${searchProductId}"][data-block-id="${currentBlockId}"]`
        );
        if (match) return match;
      }
      
      // If no match by block-id, match by product-id only (in case block-id changes or is missing)
      if (searchProductId) {
        const match = container.querySelector(
          `product-price[data-product-id="${searchProductId}"]`
        );
        if (match) return match;
      }
      
      // Fallback to first product-price if we can't match by attributes (for backwards compatibility)
      return container.querySelector('product-price');
    };
    
    // Search for the price element in the fetched HTML
    // When using section_id, Shopify returns just the section HTML wrapped in the section container
    // When not using section_id, it returns the full page HTML
    // Try multiple search strategies to find the price element
    
    // Strategy 1: Search within the product-information section (most common for product pages)
    let matchingPriceElement = null;
    const productInformationSection = event.detail.data.html.querySelector('.product-information, #product-information');
    if (productInformationSection) {
      matchingPriceElement = findPriceElement(productInformationSection);
    }
    
    // Strategy 2: Search within any shopify-section element (for section fragment responses)
    if (!matchingPriceElement) {
      const sectionElement = event.detail.data.html.querySelector('.shopify-section, [class*="section"]');
      if (sectionElement) {
        matchingPriceElement = findPriceElement(sectionElement);
      }
    }
    
    // Strategy 3: Search within main element (for full page responses)
    if (!matchingPriceElement) {
      const mainElement = event.detail.data.html.querySelector('main');
      if (mainElement) {
        matchingPriceElement = findPriceElement(mainElement);
      }
    }
    
    // Strategy 4: Search within body element
    if (!matchingPriceElement) {
      const bodyElement = event.detail.data.html.querySelector('body');
      if (bodyElement) {
        matchingPriceElement = findPriceElement(bodyElement);
      }
    }
    
    // Strategy 5: Fallback to searching the entire document
    if (!matchingPriceElement) {
      matchingPriceElement = findPriceElement(event.detail.data.html);
    }
    
    console.log('[ProductPrice] Search result', {
      foundPriceElement: !!matchingPriceElement,
      htmlStructure: event.detail.data.html.documentElement?.innerHTML?.substring(0, 500),
      allProductPrices: Array.from(event.detail.data.html.querySelectorAll('product-price')).map(el => ({
        productId: el.dataset.productId,
        blockId: el.dataset.blockId,
        priceText: el.querySelector('[ref="priceContainer"]')?.textContent?.trim()
      }))
    });
    
    if (!matchingPriceElement) {
      console.warn('[ProductPrice] No matching price element found in fetched HTML');
      return;
    }
    
    const newPrice = matchingPriceElement.querySelector('[ref="priceContainer"]');
    const currentPrice = this.querySelector('[ref="priceContainer"]');

    console.log('[ProductPrice] Price comparison', {
      currentPriceHTML: currentPrice?.innerHTML,
      newPriceHTML: newPrice?.innerHTML,
      currentPriceText: currentPrice?.textContent?.trim(),
      newPriceText: newPrice?.textContent?.trim(),
      willUpdate: currentPrice && newPrice && currentPrice.innerHTML !== newPrice.innerHTML
    });

    if (!newPrice || !currentPrice) {
      console.warn('[ProductPrice] Missing price containers', { newPrice: !!newPrice, currentPrice: !!currentPrice });
      return;
    }

    if (currentPrice.innerHTML !== newPrice.innerHTML) {
      console.log('[ProductPrice] Updating price from', currentPrice.textContent?.trim(), 'to', newPrice.textContent?.trim());
      currentPrice.replaceWith(newPrice.cloneNode(true));
    } else {
      console.log('[ProductPrice] Prices are identical, no update needed');
    }
  };
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
  console.log('[ProductPrice] Custom element registered');
  
  // Check if any product-price elements already exist in the DOM
  const existingElements = document.querySelectorAll('product-price');
  console.log('[ProductPrice] Checking for existing elements in DOM', {
    count: existingElements.length,
    elements: Array.from(existingElements).map(el => ({
      productId: el.dataset.productId,
      blockId: el.dataset.blockId,
      isConnected: el.isConnected,
      hasPriceContainer: !!el.querySelector('[ref="priceContainer"]')
    }))
  });
  
  // Set up MutationObserver to detect when product-price elements are added to DOM
  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType === 1) { // Element node
          // Check if the added node is a product-price
          if (node.tagName === 'PRODUCT-PRICE' || node.tagName?.toLowerCase() === 'product-price') {
            console.log('[ProductPrice] Element detected being added to DOM', {
              element: node,
              productId: node.dataset.productId,
              blockId: node.dataset.blockId,
              priceText: node.querySelector('[ref="priceContainer"]')?.textContent?.trim()
            });
          }
          // Check if any product-price is added within the node
          const productPrices = node.querySelectorAll?.('product-price');
          if (productPrices && productPrices.length > 0) {
            console.log('[ProductPrice] Product price elements found in added node', {
              count: productPrices.length,
              elements: Array.from(productPrices).map(el => ({
                productId: el.dataset.productId,
                blockId: el.dataset.blockId,
                priceText: el.querySelector('[ref="priceContainer"]')?.textContent?.trim()
              }))
            });
          }
        }
      });
    });
  });
  
  // Start observing
  observer.observe(document.body || document.documentElement, {
    childList: true,
    subtree: true
  });
  
  // Also check after delays in case elements are added dynamically
  setTimeout(() => {
    const delayedElements = document.querySelectorAll('product-price');
    console.log('[ProductPrice] Delayed check (1s) for elements', {
      count: delayedElements.length,
      elements: Array.from(delayedElements).map(el => ({
        productId: el.dataset.productId,
        blockId: el.dataset.blockId,
        isConnected: el.isConnected,
        hasPriceContainer: !!el.querySelector('[ref="priceContainer"]'),
        priceText: el.querySelector('[ref="priceContainer"]')?.textContent?.trim()
      }))
    });
  }, 1000);
  
  setTimeout(() => {
    const delayedElements = document.querySelectorAll('product-price');
    console.log('[ProductPrice] Delayed check (3s) for elements', {
      count: delayedElements.length,
      elements: Array.from(delayedElements).map(el => ({
        productId: el.dataset.productId,
        blockId: el.dataset.blockId,
        isConnected: el.isConnected,
        hasPriceContainer: !!el.querySelector('[ref="priceContainer"]'),
        priceText: el.querySelector('[ref="priceContainer"]')?.textContent?.trim()
      }))
    });
  }, 3000);
} else {
  console.log('[ProductPrice] Custom element already registered');
}
